/**
 * `<Avatar/>` + `<AvatarRow/>` — assignee initials circles.
 *
 * Issue #759 (Phase 2). Renders the initials circles from the wireframe
 * (BK / AT / …). Multiple assignees sit in a row; beyond a small cap they
 * overflow to a "+N" chip (B0 legend: `(+2)`). Display-only — an avatar is
 * "who's on this node", never a score about them (§A9).
 *
 * Editorial revamp (#1043, Wave 1): theme-aware.
 *   - LEGACY (flag off): the soft-mint look — alternating mint fills
 *     (`avatarA`/`avatarB`) with dark-ink initials. Byte-identical to today.
 *   - EDITORIAL (flag on): the wireframe `.t-av` look — a SATURATED fill
 *     picked deterministically from the editorial avatar palette by the
 *     member's initials, WHITE initials, and a `--surface` (cream) ring so
 *     overlapping avatars read as separate discs.
 */

import type { TaskMember } from "../members/useTaskTeamMembers";
import { useMapTheme } from "../theme/MapThemeContext";

const MAX_VISIBLE = 3;

/**
 * The editorial avatar tones (`tarii.css` `--av-*`): cobalt · sage · amber ·
 * purple · terracotta. Picked deterministically from a member's initials so
 * the same person is always the same colour across every surface (hero card,
 * drawer, digest, …) — part of the "one node reads identically everywhere"
 * integration invariant.
 */
const EDITORIAL_AVATAR_TONES = ["#4566A0", "#5E8E5C", "#C9852D", "#8C5BA1", "#B0593D"] as const;

/** Stable index into the editorial tone palette from a member's initials. */
function editorialTone(initials: string): string {
  let hash = 0;
  for (let i = 0; i < initials.length; i++) {
    hash = (hash * 31 + initials.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(hash) % EDITORIAL_AVATAR_TONES.length;
  return EDITORIAL_AVATAR_TONES[idx] as string;
}

function Avatar({ member, index, size }: { member: TaskMember; index: number; size: number }) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  const fill = editorial ? editorialTone(member.initials) : index % 2 === 0 ? c.avatarA : c.avatarB;
  return (
    <span
      title={member.name}
      data-testid="task-avatar"
      className="inline-flex shrink-0 items-center justify-center rounded-full font-bold"
      style={{
        width: size,
        height: size,
        backgroundColor: fill,
        // Editorial: white initials on a saturated disc, with a cream ring.
        color: editorial ? "#FFFFFF" : c.ink,
        fontSize: size <= 22 ? 9 : 10,
        fontWeight: editorial ? 600 : undefined,
        border: editorial ? `1.5px solid ${c.cardBg}` : undefined,
        fontFamily: editorial ? theme.fonts.sans : undefined,
      }}
    >
      {member.initials}
    </span>
  );
}

export function AvatarRow({
  members,
  size = 22,
}: {
  members: TaskMember[];
  size?: number;
}) {
  const theme = useMapTheme();
  const c = theme.palette;
  const editorial = theme.mode === "editorial";
  if (members.length === 0) return null;
  const visible = members.slice(0, MAX_VISIBLE);
  const overflow = members.length - visible.length;
  return (
    <span className="inline-flex items-center gap-1" data-testid="task-avatar-row">
      {visible.map((m, i) => (
        <Avatar key={m.membershipId} member={m} index={i} size={size} />
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex shrink-0 items-center justify-center rounded-full font-semibold"
          style={{
            width: size,
            height: size,
            backgroundColor: editorial ? c.bgRecess : c.avatarB,
            color: editorial ? c.muted : c.muted,
            fontSize: 9,
            border: editorial ? `1.5px solid ${c.cardBg}` : undefined,
            fontFamily: editorial ? theme.fonts.sans : undefined,
          }}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
